import { Download, Eye, FileCode, FileSpreadsheet, FileText, Image, Loader2 } from "lucide-react"
import { useState } from "react"
import { PreviewItem, PreviewModal } from "@/components/common/PreviewModal"
import { useAuth } from "@/hooks/useAuth"
import {
  DownloadExpiredError,
  getFreshDownloadUrl,
  s3KeyFromGeneratedUrl,
} from "@/services/uploadService"

interface FileDownloadCardProps {
  href: string
  filename: string
}

const FILE_TYPE_CONFIG: Record<string, { icon: typeof FileText; label: string; color: string }> = {
  pptx: { icon: FileText, label: "PowerPoint", color: "text-orange-600" },
  xlsx: { icon: FileSpreadsheet, label: "Excel", color: "text-green-600" },
  docx: { icon: FileText, label: "Word", color: "text-blue-600" },
  pdf: { icon: FileText, label: "PDF", color: "text-red-600" },
  mp3: { icon: FileText, label: "Audio", color: "text-teal-600" },
  png: { icon: Image, label: "Image", color: "text-purple-600" },
  jpg: { icon: Image, label: "Image", color: "text-purple-600" },
  jpeg: { icon: Image, label: "Image", color: "text-purple-600" },
  html: { icon: FileCode, label: "HTML", color: "text-amber-600" },
  htm: { icon: FileCode, label: "HTML", color: "text-amber-600" },
}

function getFileType(filename: string) {
  const ext = filename.split(".").pop()?.toLowerCase() || ""
  return FILE_TYPE_CONFIG[ext] || { icon: FileText, label: "File", color: "text-gray-600" }
}

export function FileDownloadCard({ href, filename }: FileDownloadCardProps) {
  const { icon: Icon, label, color } = getFileType(filename)
  const { token } = useAuth()
  const [busy, setBusy] = useState(false)
  const [viewing, setViewing] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [preview, setPreview] = useState<PreviewItem | null>(null)
  const fileKey = s3KeyFromGeneratedUrl(href)
  // HTML 成果物はダウンロードせずその場で確認できる（2026-07-16）。Board の成果物
  // プレビューと同じ PreviewModal（html-file 種別・sandbox iframe）を流用する。
  const isHtml = /\.html?$/i.test(filename)

  // チャット本文に埋め込まれた署名付き URL は約1時間で失効する（2026-07-07 実走FB:
  // 翌日の履歴から開けない）。クリック時に認証付き API でその場の新しい署名付き URL を
  // 取得して開く。キーが取れない URL（generated/ 以外）や未認証時は従来の直リンク。
  const handleDownload = async (e: React.MouseEvent) => {
    if (!fileKey || !token) return
    e.preventDefault()
    if (busy || viewing) return
    setBusy(true)
    setNotice(null)
    try {
      // .html は保存 ContentType 次第でタブ表示になりダウンロードにならないため attachment 明示
      const fresh = isHtml
        ? await getFreshDownloadUrl(fileKey, token, "attachment")
        : await getFreshDownloadUrl(fileKey, token)
      window.open(fresh, "_blank", "noopener,noreferrer")
    } catch (err) {
      setNotice(
        err instanceof DownloadExpiredError
          ? "保存期間（7日）を過ぎたため取得できません。必要ならもう一度作成を頼んでください。"
          : "リンクの取得に失敗しました。時間をおいて再度お試しください。"
      )
    } finally {
      setBusy(false)
    }
  }

  const handlePreview = async () => {
    if (!fileKey || !token || busy || viewing) return
    setViewing(true)
    setNotice(null)
    try {
      const fresh = await getFreshDownloadUrl(fileKey, token, "inline")
      setPreview({ name: filename, previewKind: "html-file", previewUrl: fresh })
    } catch (err) {
      setNotice(
        err instanceof DownloadExpiredError
          ? "保存期間（7日）を過ぎたため取得できません。必要ならもう一度作成を頼んでください。"
          : "リンクの取得に失敗しました。時間をおいて再度お試しください。"
      )
    } finally {
      setViewing(false)
    }
  }

  return (
    <div className="my-2 p-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3">
        <div className={`flex-shrink-0 ${color}`}>
          <Icon size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">{filename}</div>
          <div className="text-xs text-gray-500">{label}</div>
        </div>
        {isHtml && fileKey && token && (
          <button
            type="button"
            onClick={handlePreview}
            className="flex-shrink-0 p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title={`${filename} をその場で見る`}
          >
            {viewing ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
          </button>
        )}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleDownload}
          className="flex-shrink-0 p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          title={`${filename} をダウンロード`}
        >
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
        </a>
      </div>
      {notice && <div className="mt-2 text-xs text-red-600">{notice}</div>}
      {preview && <PreviewModal item={preview} onClose={() => setPreview(null)} />}
    </div>
  )
}
